/**
 * Plan operations: the only way a plan changes. The board, the REST API, the MCP server and
 * the CLI all apply these same pure functions, so a drag on the board and an agent's batch
 * edit behave identically. Ops that create things carry their new IDs, so every replica that
 * applies an op ends up with the same state.
 */
import { z } from "zod";
const segIdSchema = z.string().min(1);
const anyRecord = z.record(z.string(), z.any());
const nameSchema = z.string().trim().min(1).max(200);
const pdSchema = z.number().min(0.5).max(10000);
export const opSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("move"),
        segId: segIdSchema,
        from: z.string(),
        to: z.string(),
        /** Target row lane: 0 = flow lane, pin-track lanes route to the flow lane, beyond = free-float lanes (one past the end adds a lane). */
        lane: z.number().int().min(0),
        /** Workday under the drop point. */
        day: z.number().min(0),
    }),
    z.object({ type: z.literal("togglePin"), person: z.string(), segId: segIdSchema }),
    z.object({ type: z.literal("splitChunk"), segId: segIdSchema, newId: segIdSchema }),
    z.object({ type: z.literal("resizeChunk"), segId: segIdSchema, delta: z.number() }),
    z.object({ type: z.literal("deleteChunk"), segId: segIdSchema }),
    z.object({ type: z.literal("setUtil"), person: z.string(), value: z.number().min(0.05).max(1) }),
    z.object({ type: z.literal("setWsOrg"), org: z.record(z.string(), z.array(z.string())) }),
    z.object({ type: z.literal("reschedule"), backlog: z.array(anyRecord) }),
    z.object({
        type: z.literal("replaceState"),
        layout: anyRecord,
        pins: anyRecord,
        util: z.record(z.string(), z.number()).optional(),
        wsOrg: z.record(z.string(), z.array(z.string())).optional(),
        backlog: z.array(anyRecord).nullable().optional(),
        reason: z.string().optional(),
    }),
    // initiatives (backlog workstreams), epics (backlog items) and tasks (an epic's work in one discipline)
    z.object({ type: z.literal("addInitiative"), name: nameSchema, team: z.number().int() }),
    z.object({ type: z.literal("updateInitiative"), name: z.string(), newName: nameSchema.optional(), team: z.number().int().optional() }),
    z.object({ type: z.literal("deleteInitiative"), name: z.string() }),
    z.object({
        type: z.literal("addEpic"),
        epic: z.object({ n: nameSchema, w: nameSchema, p: z.string().min(1), s: z.boolean(), i: z.string().default("") }),
    }),
    z.object({
        type: z.literal("updateEpic"),
        name: z.string(),
        patch: z.object({ n: nameSchema, w: nameSchema, p: z.string().min(1), s: z.boolean(), i: z.string() }).partial(),
    }),
    z.object({ type: z.literal("deleteEpic"), name: z.string() }),
    z.object({ type: z.literal("addTask"), epic: z.string(), disc: z.string(), pd: pdSchema, person: z.string(), segId: segIdSchema }),
    z.object({ type: z.literal("updateTask"), epic: z.string(), disc: z.string(), pd: pdSchema.optional(), person: z.string().optional() }),
    z.object({ type: z.literal("deleteTask"), epic: z.string(), disc: z.string() }),
]);
export class OpError extends Error {
    constructor(message) { super(message); this.name = "OpError"; }
}
const clone = (v) => JSON.parse(JSON.stringify(v));
const baseId = (id) => id.split("~")[0];
export function findSeg(layout, id) {
    for (const person of Object.keys(layout)) {
        const e = layout[person];
        let i = e.free.findIndex(s => s.id === id);
        if (i >= 0)
            return { person, list: e.free, index: i, seg: e.free[i], where: "free" };
        for (const l of e.extra) {
            i = l.findIndex(s => s.id === id);
            if (i >= 0)
                return { person, list: l, index: i, seg: l[i], where: "extra" };
        }
        i = e.pins.findIndex(s => s.id === id);
        if (i >= 0)
            return { person, list: e.pins, index: i, seg: e.pins[i], where: "pins" };
    }
    return null;
}
const pruneEmptyLanes = (layout) => {
    for (const k of Object.keys(layout)) {
        const ex = layout[k].extra;
        while (ex.length && ex[ex.length - 1].length === 0)
            ex.pop();
    }
};
/** Every brick in a plan: flow lanes, free-float lanes, pinned bricks and the pin registry. */
function* allSegs(state) {
    for (const e of Object.values(state.layout)) {
        yield* e.free;
        yield* e.pins;
        for (const l of e.extra)
            yield* l;
    }
    yield* Object.values(state.pins);
}
/** Removes every brick matching pred, wherever it sits. */
function removeSegs(state, pred) {
    for (const e of Object.values(state.layout)) {
        e.free = e.free.filter(s => !pred(s));
        e.pins = e.pins.filter(s => !pred(s));
        e.extra = e.extra.map(l => l.filter(s => !pred(s)));
    }
    for (const [id, s] of Object.entries(state.pins))
        if (pred(s))
            delete state.pins[id];
    pruneEmptyLanes(state.layout);
}
const sameName = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();
const taskKey = (epic, disc) => `${epic}|${disc}`;
function epicOf(state, name) {
    const item = (state.backlog || []).find(t => sameName(t.n, name));
    if (!item)
        throw new OpError(`No epic "${name}"`);
    return item;
}
/** Puts a new brick into a person's flow lane ahead of the first lower-priority brick, the way the scheduler orders work. */
function insertByPriority(engine, list, seg) {
    const pri = Object.fromEntries(engine.config.priorities.map(p => [p.key, p]));
    const rank = (s) => [s.isN ? 1 : 0, pri[s.pri]?.tier ?? Infinity, pri[s.pri]?.rank ?? Infinity];
    const r = rank(seg);
    const later = (s) => { const k = rank(s); for (let i = 0; i < k.length; i++)
        if (k[i] !== r[i])
            return k[i] > r[i]; return false; };
    const at = list.findIndex(later);
    if (at < 0)
        list.push(seg);
    else
        list.splice(at, 0, seg);
}
/** Initiative names in use: from the backlog, the bricks and the sidebar order. */
function initiativeNames(state) {
    const out = new Set();
    for (const t of state.backlog || [])
        out.add(t.w.trim());
    for (const s of allSegs(state))
        out.add(s.ws.trim());
    for (const list of Object.values(state.wsOrg))
        for (const w of list)
            out.add(w.trim());
    return [...out];
}
/** Applies one op and returns the new state; the input is never mutated. Throws OpError when the op cannot apply. */
export function applyOp(engine, state, op) {
    const next = clone(state);
    const L = next.layout;
    const util = (k) => next.util[k] ?? engine.defaultUtil(k);
    const need = (k) => { if (!L[k])
        throw new OpError(`Unknown person "${k}"`); return L[k]; };
    switch (op.type) {
        case "move": {
            need(op.from);
            const tgt = need(op.to);
            const id = baseId(op.segId);
            const res = findSeg(L, id);
            if (!res || res.person !== op.from)
                throw new OpError(`Chunk ${op.segId} is not on ${op.from}'s row`);
            const fromR = engine.config.resources.find(r => r.key === op.from);
            const toR = engine.config.resources.find(r => r.key === op.to);
            if (fromR.discipline !== toR.discipline)
                throw new OpError(`${op.to} is not a ${fromR.discipline} resource`);
            // positions before the move decide where it lands
            const resolvedTo = engine.simPerson(op.to, state.layout[op.to], util(op.to));
            const ov = engine.pinTracks(state.layout[op.to].pins, op.to, util(op.to)).length;
            const lanes = 1 + ov + state.layout[op.to].extra.length;
            const lane = Math.min(op.lane, lanes);
            const moved = res.list.splice(res.index, 1)[0];
            if (res.where === "pins") {
                moved.pinned = false;
                delete next.pins[moved.id];
            }
            const extraIdx = lane <= ov ? -1 : lane - 1 - ov; // drops on pin tracks route to the flow lane
            while (extraIdx >= 0 && tgt.extra.length < extraIdx + 1)
                tgt.extra.push([]);
            const list = extraIdx < 0 ? tgt.free : tgt.extra[extraIdx];
            if (extraIdx >= 0)
                moved.anchorDay = Math.round(op.day * 2) / 2; // free-float lanes anchor where dropped
            else
                delete moved.anchorDay; // the flow lane has gravity
            let idx = 0;
            if (extraIdx < 0) {
                for (const s of resolvedTo)
                    if (s.lane === 0 && !s.pinned && baseId(s.id) !== id && (s.d0 + s.d1) / 2 < op.day)
                        idx++;
            }
            else {
                const d0ById = {};
                for (const s of resolvedTo)
                    if (s.lane === lane)
                        d0ById[baseId(s.id)] = s.d0;
                for (const item of list) {
                    const at = item.anchorDay ?? d0ById[item.id];
                    if (at !== undefined && at < op.day)
                        idx++;
                }
            }
            list.splice(Math.min(idx, list.length), 0, moved);
            pruneEmptyLanes(L);
            return next;
        }
        case "togglePin": {
            const e = need(op.person);
            const id = baseId(op.segId);
            const pinIdx = e.pins.findIndex(s => s.id === id);
            const resolved = engine.simPerson(op.person, state.layout[op.person], util(op.person));
            if (pinIdx >= 0) {
                const s = e.pins.splice(pinIdx, 1)[0];
                s.pinned = false;
                delete next.pins[s.id];
                const at = resolved.find(x => baseId(x.id) === id)?.d0 ?? 0;
                // drop into the flow lane at its current day so unpinning doesn't move it
                const pieces = {};
                for (const x of resolved)
                    if (x.lane === 0) {
                        const p = (pieces[baseId(x.id)] ??= { d0: 1e9, d1: 0 });
                        p.d0 = Math.min(p.d0, x.d0);
                        p.d1 = Math.max(p.d1, x.d1);
                    }
                let idx = 0;
                for (const item of e.free) {
                    const p = pieces[item.id];
                    if (p && (p.d0 + p.d1) / 2 < at)
                        idx++;
                }
                e.free.splice(idx, 0, s);
            }
            else {
                let s = null;
                const i = e.free.findIndex(x => x.id === id);
                if (i >= 0)
                    s = e.free.splice(i, 1)[0];
                else
                    for (const l of e.extra) {
                        const j = l.findIndex(x => x.id === id);
                        if (j >= 0) {
                            s = l.splice(j, 1)[0];
                            break;
                        }
                    }
                if (!s)
                    throw new OpError(`Chunk ${op.segId} is not on ${op.person}'s row`);
                pruneEmptyLanes(L);
                const d0 = resolved.find(x => baseId(x.id) === id)?.d0 ?? 0;
                const pinSeg = { ...s, pinned: true, person: op.person, startPd: engine.dayToPinPd(op.person, d0), part: 0 };
                delete pinSeg.anchorDay;
                e.pins.push(pinSeg);
                next.pins[s.id] = pinSeg;
            }
            return next;
        }
        case "splitChunk": {
            const hit = findSeg(L, op.segId);
            if (!hit)
                throw new OpError(`No chunk ${op.segId}`);
            if (hit.seg.pd <= 1.5)
                throw new OpError("Main chunk too small to split");
            if (findSeg(L, op.newId))
                throw new OpError(`Chunk id ${op.newId} already exists`);
            hit.seg.pd -= 1;
            if (hit.seg.pinned && next.pins[hit.seg.id])
                next.pins[hit.seg.id] = { ...next.pins[hit.seg.id], pd: hit.seg.pd };
            const sub = { ...hit.seg, id: op.newId, pd: 1, isSub: true, parent: hit.seg.id, pinned: false };
            delete sub.startPd;
            delete sub.person;
            delete sub.anchorDay;
            delete sub.part;
            if (hit.seg.pinned)
                L[hit.person].free.push(sub); // subs are flow work even off a pinned main
            else
                hit.list.splice(hit.index + 1, 0, sub); // scheduled right after the primary
            return next;
        }
        case "resizeChunk": {
            const sub = findSeg(L, op.segId);
            if (!sub?.seg.isSub || !sub.seg.parent)
                throw new OpError(`${op.segId} is not a sub-chunk`);
            const main = findSeg(L, sub.seg.parent);
            if (!main)
                throw new OpError(`Primary chunk ${sub.seg.parent} is missing`);
            if (op.delta > 0 && main.seg.pd - op.delta < 1 - 1e-9)
                throw new OpError("Primary chunk would drop below 1 day");
            if (op.delta < 0 && sub.seg.pd + op.delta < 1 - 1e-9)
                throw new OpError("Sub-chunk would drop below 1 day");
            sub.seg.pd += op.delta;
            main.seg.pd -= op.delta;
            for (const h of [main, sub])
                if (h.seg.pinned && next.pins[h.seg.id])
                    next.pins[h.seg.id] = { ...next.pins[h.seg.id], pd: h.seg.pd };
            return next;
        }
        case "deleteChunk": {
            const sub = findSeg(L, op.segId);
            if (!sub?.seg.isSub)
                throw new OpError(`${op.segId} is not a sub-chunk; only sub-chunks can be deleted`);
            sub.list.splice(sub.index, 1);
            if (sub.seg.pinned)
                delete next.pins[sub.seg.id];
            const main = sub.seg.parent ? findSeg(L, sub.seg.parent) : null;
            if (main) {
                main.seg.pd += sub.seg.pd;
                if (main.seg.pinned && next.pins[main.seg.id])
                    next.pins[main.seg.id] = { ...next.pins[main.seg.id], pd: main.seg.pd };
            }
            pruneEmptyLanes(L);
            return next;
        }
        case "setUtil": {
            need(op.person);
            next.util[op.person] = Math.min(1, Math.max(0.1, op.value));
            return next;
        }
        case "setWsOrg": {
            next.wsOrg = clone(op.org);
            return next;
        }
        case "reschedule": {
            const backlog = op.backlog;
            next.backlog = backlog;
            next.layout = engine.scheduleAll(backlog, next.pins, next.util);
            return next;
        }
        case "replaceState": {
            const known = new Set(engine.config.resources.map(r => r.key));
            const unknown = Object.entries(op.layout)
                .filter(([k, e]) => !known.has(k) && (e?.free?.length || e?.pins?.length || e?.extra?.some(l => l.length)))
                .map(([k]) => k);
            if (unknown.length)
                throw new OpError(`This board has work for people not in the project: ${unknown.join(", ")}. Add them in settings first.`);
            next.layout = normalizeLayout(engine, op.layout);
            next.pins = clone(op.pins);
            if (op.util)
                next.util = { ...op.util };
            if (op.wsOrg)
                next.wsOrg = clone(op.wsOrg);
            if (op.backlog !== undefined)
                next.backlog = clone(op.backlog);
            return next;
        }
        case "addInitiative": {
            if (initiativeNames(next).some(w => sameName(w, op.name)))
                throw new OpError(`There is already an initiative called "${op.name}"`);
            if (!engine.config.teams.some(t => t.id === op.team))
                throw new OpError(`Unknown team ${op.team}`);
            (next.wsOrg[op.team] ??= []).push(op.name.trim());
            return next;
        }
        case "updateInitiative": {
            const from = initiativeNames(next).find(w => sameName(w, op.name));
            if (!from)
                throw new OpError(`No initiative "${op.name}"`);
            let to = from;
            if (op.newName && op.newName.trim() !== from) {
                to = op.newName.trim();
                if (!sameName(to, from) && initiativeNames(next).some(w => sameName(w, to)))
                    throw new OpError(`There is already an initiative called "${to}"`);
                for (const t of next.backlog || [])
                    if (sameName(t.w, from))
                        t.w = to;
                for (const s of allSegs(next))
                    if (sameName(s.ws, from))
                        s.ws = to;
                for (const k of Object.keys(next.wsOrg))
                    next.wsOrg[k] = next.wsOrg[k].map(w => (sameName(w, from) ? to : w));
            }
            if (op.team !== undefined) {
                if (!engine.config.teams.some(t => t.id === op.team))
                    throw new OpError(`Unknown team ${op.team}`);
                for (const k of Object.keys(next.wsOrg))
                    next.wsOrg[k] = next.wsOrg[k].filter(w => !sameName(w, to));
                (next.wsOrg[op.team] ??= []).push(to);
            }
            return next;
        }
        case "deleteInitiative": {
            if (!initiativeNames(next).some(w => sameName(w, op.name)))
                throw new OpError(`No initiative "${op.name}"`);
            if (next.backlog)
                next.backlog = next.backlog.filter(t => !sameName(t.w, op.name));
            removeSegs(next, s => sameName(s.ws, op.name));
            for (const k of Object.keys(next.wsOrg))
                next.wsOrg[k] = next.wsOrg[k].filter(w => !sameName(w, op.name));
            return next;
        }
        case "addEpic": {
            const backlog = (next.backlog ??= []);
            if (backlog.some(t => sameName(t.n, op.epic.n)))
                throw new OpError(`There is already an epic called "${op.epic.n}"`);
            const order = backlog.reduce((m, t) => Math.max(m, t.d || 0), 0) + 1;
            backlog.push({ p: op.epic.p, d: order, w: op.epic.w.trim(), n: op.epic.n.trim(), i: op.epic.i, s: op.epic.s, e: {} });
            return next;
        }
        case "updateEpic": {
            const item = epicOf(next, op.name);
            const old = item.n;
            const p = op.patch;
            if (p.n !== undefined && !sameName(p.n, old) && (next.backlog || []).some(t => sameName(t.n, p.n)))
                throw new OpError(`There is already an epic called "${p.n}"`);
            if (p.n !== undefined)
                item.n = p.n.trim();
            if (p.w !== undefined)
                item.w = p.w.trim();
            if (p.p !== undefined)
                item.p = p.p;
            if (p.s !== undefined)
                item.s = p.s;
            if (p.i !== undefined)
                item.i = p.i;
            for (const s of allSegs(next)) {
                if (!sameName(s.task, old))
                    continue;
                s.task = item.n;
                s.key = taskKey(item.n, s.disc);
                s.ws = item.w;
                s.pri = item.p;
                s.isN = !item.s;
                s.intended = item.i;
            }
            return next;
        }
        case "deleteEpic": {
            const item = epicOf(next, op.name);
            next.backlog = (next.backlog || []).filter(t => t !== item);
            removeSegs(next, s => sameName(s.task, item.n));
            return next;
        }
        case "addTask": {
            const item = epicOf(next, op.epic);
            if (item.e[op.disc] !== undefined)
                throw new OpError(`"${item.n}" already has ${op.disc} work`);
            if (!engine.config.disciplines.some(d => d.key === op.disc))
                throw new OpError(`Unknown discipline "${op.disc}"`);
            const who = engine.config.resources.find(r => r.key === op.person);
            if (!who)
                throw new OpError(`Unknown person "${op.person}"`);
            if (who.discipline !== op.disc)
                throw new OpError(`${who.name} is not a ${op.disc} resource`);
            if ([...allSegs(next)].some(s => s.id === op.segId))
                throw new OpError(`Chunk id ${op.segId} already exists`);
            item.e[op.disc] = op.pd;
            insertByPriority(engine, (L[op.person] ??= { free: [], pins: [], extra: [] }).free, {
                id: op.segId, key: taskKey(item.n, op.disc), task: item.n, ws: item.w, pri: item.p, disc: op.disc, pd: op.pd, isN: !item.s, intended: item.i,
            });
            return next;
        }
        case "updateTask": {
            const item = epicOf(next, op.epic);
            if (item.e[op.disc] === undefined)
                throw new OpError(`"${item.n}" has no ${op.disc} work`);
            const key = taskKey(item.n, op.disc);
            if (op.person !== undefined) {
                const who = engine.config.resources.find(r => r.key === op.person);
                if (!who)
                    throw new OpError(`Unknown person "${op.person}"`);
                if (who.discipline !== op.disc)
                    throw new OpError(`${who.name} is not a ${op.disc} resource`);
                const moving = [];
                for (const e of Object.values(L)) {
                    moving.push(...e.free.filter(s => s.key === key), ...e.pins.filter(s => s.key === key), ...e.extra.flatMap(l => l.filter(s => s.key === key)));
                }
                removeSegs(next, s => s.key === key);
                for (const s of moving) {
                    const seg = { ...s, pinned: false };
                    delete seg.person;
                    delete seg.startPd;
                    delete seg.part;
                    delete seg.anchorDay;
                    insertByPriority(engine, (L[op.person] ??= { free: [], pins: [], extra: [] }).free, seg);
                }
            }
            if (op.pd !== undefined) {
                const segs = [...allSegs(next)].filter(s => s.key === key && !(s.id in next.pins && next.pins[s.id] === s));
                const subs = segs.filter(s => s.isSub).reduce((n, s) => n + s.pd, 0);
                const main = segs.find(s => !s.isSub);
                if (main) {
                    if (op.pd - subs < 0.5)
                        throw new OpError(`Linked chunks already use ${subs}pd; remove some first`);
                    main.pd = op.pd - subs;
                    if (next.pins[main.id])
                        next.pins[main.id] = { ...next.pins[main.id], pd: main.pd };
                }
                item.e[op.disc] = op.pd;
            }
            return next;
        }
        case "deleteTask": {
            const item = epicOf(next, op.epic);
            if (item.e[op.disc] === undefined)
                throw new OpError(`"${item.n}" has no ${op.disc} work`);
            delete item.e[op.disc];
            removeSegs(next, s => s.key === taskKey(item.n, op.disc));
            return next;
        }
    }
}
/** Ensures every configured person has a row and every row has all three lists. */
export function normalizeLayout(engine, layout) {
    const out = {};
    for (const r of engine.config.resources) {
        const e = layout[r.key];
        out[r.key] = { free: e?.free ? clone(e.free) : [], pins: e?.pins ? clone(e.pins) : [], extra: e?.extra ? clone(e.extra) : [] };
    }
    return out;
}
/** A short human-readable description of an op, for the activity log. */
export function describeOp(op) {
    switch (op.type) {
        case "move": return op.from === op.to ? `Moved ${op.segId} on ${op.to}'s row` : `Moved ${op.segId} from ${op.from} to ${op.to}`;
        case "togglePin": return `Toggled pin on ${op.segId}`;
        case "splitChunk": return `Split ${op.segId}`;
        case "resizeChunk": return `Resized ${op.segId} by ${op.delta > 0 ? "+" : ""}${op.delta}d`;
        case "deleteChunk": return `Deleted sub-chunk ${op.segId}`;
        case "setUtil": return `Set ${op.person} utilization to ${Math.round(op.value * 100)}%`;
        case "setWsOrg": return "Reordered workstreams";
        case "reschedule": return `Re-scheduled from ${op.backlog.length} backlog rows`;
        case "replaceState": return op.reason || "Replaced board state";
        case "addInitiative": return `Added initiative ${op.name}`;
        case "updateInitiative": return op.newName && op.newName !== op.name ? `Renamed initiative ${op.name} to ${op.newName}` : `Edited initiative ${op.name}`;
        case "deleteInitiative": return `Deleted initiative ${op.name}`;
        case "addEpic": return `Added epic ${op.epic.n}`;
        case "updateEpic": return op.patch.n && op.patch.n !== op.name ? `Renamed epic ${op.name} to ${op.patch.n}` : `Edited epic ${op.name}`;
        case "deleteEpic": return `Deleted epic ${op.name}`;
        case "addTask": return `Added ${op.disc} task to ${op.epic} for ${op.person}`;
        case "updateTask": return `Edited ${op.disc} task on ${op.epic}`;
        case "deleteTask": return `Deleted ${op.disc} task from ${op.epic}`;
    }
}
/** Reads a board file saved by the original artifact's "Save .json" (or this app's export). */
export function planFromSavedBoard(engine, data, backlog = null) {
    const d = data;
    if (!d || typeof d !== "object" || !d.layout || !d.pins)
        throw new OpError("Not a board state file: it needs layout and pins");
    return {
        backlog,
        layout: normalizeLayout(engine, d.layout),
        pins: clone(d.pins),
        util: d.util && typeof d.util === "object" ? { ...d.util } : {},
        wsOrg: d.wsOrg && typeof d.wsOrg === "object" ? clone(d.wsOrg) : {},
    };
}
//# sourceMappingURL=plan.js.map